import { yupResolver } from "@hookform/resolvers/yup";
import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { Country } from "@omni/types/enums/country.enum";
import { Button } from "@omni/ui/components/Button/button.component";
import { Suspense } from "react";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { shallowEqual, useSelector } from "react-redux";
import * as yup from "yup";

import { useAddressValidationContext } from "@/components/Address/AddressValidation/context/addressValidation.context";
import { PhoneField } from "@/components/Form/RHF/FormFields/PhoneField/phoneField.container";
import { TextAreaField } from "@/components/Form/RHF/FormFields/TextAreaField/textAreaField.component";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { Messages } from "@/components/General/Messages/messages.container";
import * as PhonenumberPrefixesSelectors from "@/redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.selectors";
import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { BPOSTInvalidAddressMessageId } from "@/utils/address/validation.config";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import * as generalRHFValidationRules from "@/utils/validation/schemas/general.RHF";
import * as userRHFValidationRules from "@/utils/validation/schemas/user.RHF";

import { BusinessAddressFormBlock } from "../Form/RHF/FormBlocks/Address/addressFormBlock.container";
import { BusinessDefaultAddressValidationSchema } from "../Form/RHF/FormBlocks/Address/addressFormBlock.schema";
import { RequiredPhoneSchema } from "../Form/RHF/FormFields/PhoneField/phoneField.schema";
import { useOmniForm } from "../Form/RHF/Utils/useOmniForm";
import { RequestFranchiseRequest } from "./franchiseRequest.types";

const FORM_ID = "franchiseRequest.form";

const FranchiseRequestValidationSchema = yup
    .object({
        firstName: userRHFValidationRules.optionalFirstName,
        lastName: userRHFValidationRules.lastName,
        email: userRHFValidationRules.emailWithNB,
        message: generalRHFValidationRules.message(1500)
    })
    .concat(RequiredPhoneSchema)
    .concat(BusinessDefaultAddressValidationSchema)
    .required();

export type FormValues = yup.InferType<typeof FranchiseRequestValidationSchema>;

type FranchiseRequestProps = {
    isLoading: boolean;
    onSubmit: (request: RequestFranchiseRequest) => Promise<any>;
};

const FranchiseRequest = ({ isLoading, onSubmit }: FranchiseRequestProps) => {
    const { t } = useGranite();
    const { isValidating, validateBusiness } = useAddressValidationContext();
    const phonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.getPhonenumberPrefixes,
        shallowEqual
    );

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            firstName: "",
            lastName: "",
            phone: "",
            phonePrefix: "+32",
            email: "",
            message: "",
            country: Country.Be,
            validationStatus: AddressValidationStatus.NoValidationNeeded
        },
        resolver: yupResolver(FranchiseRequestValidationSchema),
        context: {
            phonenumberPrefixData: phonenumberPrefixes
        }
    });

    const handleSubmit: SubmitHandler<FormValues> = async (formValues) => {
        clearMessageById(BPOSTInvalidAddressMessageId);
        clearMessageById("franchise-request-success");
        clearMessageById("franchise-request-failure");

        const validationResult = await validateBusiness({
            companyName: `${formValues.firstName} ${formValues.lastName}`,
            street: formValues.street,
            number: formValues.number,
            zipCode: formValues.zipCode,
            city: formValues.city,
            country: formValues.country,
            box: formValues.box,
            isBpostField: !!formValues.selected,
            currentStatus: AddressValidationStatus.Unknown
        });

        if (validationResult === AddressValidationStatus.Invalid) {
            broadcastMessage({
                id: BPOSTInvalidAddressMessageId,
                text: t("bpost.validation.address.invalid"),
                status: "warning",
                dismissible: true
            });
            return;
        }

        if (validationResult === AddressValidationStatus.Closed) {
            return;
        }

        const values: RequestFranchiseRequest = {
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            street: formValues.street,
            streetNumber: formValues.number,
            box: formValues.box ? formValues.box : undefined,
            zipCode: formValues.zipCode,
            city: formValues.city,
            countryCode: formValues.country,
            email: formValues.email,
            phoneNumber: formValues.phone
                ? `${formValues.phonePrefix}${formValues.phone}`
                : undefined,
            message: formValues.message
        };

        return onSubmit(values).then(() => {
            methods.reset();
        });
    };

    return (
        <Suspense fallback={<p>{t("common.loading")}</p>}>
            <Messages messageid={BPOSTInvalidAddressMessageId} />
            <Messages messageid={"franchise-request-success"} />
            <Messages messageid={"franchise-request-failure"} />
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={true}
            >
                {({ initialized }) => (
                    <FormProvider {...methods}>
                        <form onSubmit={methods.handleSubmit(handleSubmit)}>
                            <Row>
                                <Col>
                                    <TextField<FormValues>
                                        name={"firstName"}
                                        label={t(
                                            "general.form.firstName.label"
                                        )}
                                        required={false}
                                        placeholder={t(
                                            "general.form.firstname.placeholder"
                                        )}
                                        autoComplete={"given-name"}
                                        disabled={!initialized}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <TextField<FormValues>
                                        name={"lastName"}
                                        label={t("general.form.lastName.label")}
                                        required={true}
                                        placeholder={t(
                                            "general.form.lastName.placeholder"
                                        )}
                                        autoComplete={"family-name"}
                                        disabled={!initialized}
                                    />
                                </Col>
                            </Row>

                            <BusinessAddressFormBlock<
                                FormValues,
                                AddressType.Default
                            >
                                addressType={AddressType.Default}
                                skipValidation={true}
                                addressStatus={AddressValidationStatus.Unknown}
                                disabled={!initialized}
                            />

                            <Row>
                                <Col>
                                    <PhoneField<FormValues>
                                        autoComplete={"tel-national"}
                                        label={t(
                                            "general.form.phoneNumber.label"
                                        )}
                                        placeholder={t(
                                            "general.form.phoneNumber.placeholder"
                                        )}
                                        required={true}
                                        disabled={!initialized}
                                        phonenumberPrefixes={
                                            phonenumberPrefixes
                                        }
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <TextField<FormValues>
                                        name="email"
                                        label={t("general.form.email.label")}
                                        required={true}
                                        autoComplete={"email"}
                                        placeholder={t(
                                            "general.form.email.placeholder"
                                        )}
                                        disabled={!initialized}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <TextAreaField<FormValues>
                                        name="message"
                                        label={t("general.form.message.label")}
                                        required={true}
                                        placeholder={t(
                                            "general.form.message.placeholder"
                                        )}
                                        maxLength={1500}
                                        disabled={!initialized}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Button
                                        test="franchiseRequest-button-submit"
                                        type={"submit"}
                                        loading={isLoading || isValidating}
                                        disabled={!initialized}
                                    >
                                        {t("general.form.send")}
                                    </Button>
                                </Col>
                            </Row>
                        </form>
                    </FormProvider>
                )}
            </FormInfoProvider>
        </Suspense>
    );
};

export default FranchiseRequest;
